import { Box, Typography, Paper, Grid } from "@mui/material";
import React from "react";

interface IProgressCircleProps {
    title: string;
    subtitle: string;
    progress: number;
    idealProgress: number;
    description: string;
    planned: string;
    ideal: string;
    realized: string;
    currency?: boolean;
}

export const ProgressCircle: React.FC<IProgressCircleProps> = ({
    title,
    subtitle,
    progress,
    idealProgress,
    description,
    planned,
    ideal,
    realized,
    currency = false,
}) => {
    const radius = 80;
    const circumference = 2 * Math.PI * radius;
    const dashOffset = circumference * (1 - progress / 100);

    const formatPercentage = (value: number) => {
        const formattedValue = (value / 100).toLocaleString("pt-BR", {
            style: "percent",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        });
        return formattedValue;
    };

    return (
        <Paper
            elevation={0}
            sx={{ p: 3, borderRadius: 2, "background-color": "unset" }}
        >
            <Box
                sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}
            >
                <Box>
                    <Typography variant="h5" color="text.primary">
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {subtitle}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ display: "flex", alignItems: "center" }}>
                <Box
                    sx={{
                        position: "relative",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <svg width="180" height="180" viewBox="0 0 180 180">
                        {/* Background circle (light gray) */}
                        <circle
                            cx="90"
                            cy="90"
                            r={radius}
                            fill="none"
                            stroke="#f5f5f5"
                            strokeWidth="16"
                        />

                        {/* Ideal progress circle (lighter green) */}
                        <circle
                            cx="90"
                            cy="90"
                            r={radius}
                            fill="none"
                            stroke="#66bb6a"
                            strokeWidth="16"
                            strokeDasharray={circumference}
                            strokeDashoffset={
                                circumference * (1 - idealProgress / 100)
                            }
                            transform="rotate(-90 90 90)"
                        />

                        {/* Actual progress circle (darker green) */}
                        <circle
                            cx="90"
                            cy="90"
                            r={radius}
                            fill="none"
                            stroke="#2e7d32"
                            strokeWidth="16"
                            strokeDasharray={circumference}
                            strokeDashoffset={dashOffset}
                            transform="rotate(-90 90 90)"
                        />

                        {/* Inner circle (white background for text) */}
                        <circle cx="90" cy="90" r="65" fill="white" />
                    </svg>

                    <Box
                        sx={{
                            position: "absolute",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center",
                        }}
                    >
                        <Typography
                            variant="body2"
                            color="success.light"
                            sx={{ mb: 0.5 }}
                        >
                            Ideal - {formatPercentage(idealProgress || 0)}
                        </Typography>
                        <Typography variant="body2" color="success.dark">
                            Concluído - {formatPercentage(progress || 0)}
                        </Typography>
                    </Box>
                </Box>

                <Box sx={{ ml: 3, flex: 1 }}>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mb: 2 }}
                    >
                        {description}
                    </Typography>

                    <Grid container spacing={1}>
                        <Grid size={7}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {currency
                                    ? "Custo previsto:"
                                    : "Horas previstas:"}
                            </Typography>
                        </Grid>
                        <Grid size={7}>
                            <Typography variant="body2" fontWeight={500}>
                                {planned}
                            </Typography>
                        </Grid>

                        <Grid size={7}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {currency ? "Ideal hoje:" : "Ideal hoje:"}
                            </Typography>
                        </Grid>
                        <Grid size={7}>
                            <Typography variant="body2" fontWeight={500}>
                                {ideal}
                            </Typography>
                        </Grid>

                        <Grid size={7}>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                {currency
                                    ? "Custo realizado:"
                                    : "Horas realizadas:"}
                            </Typography>
                        </Grid>
                        <Grid size={7}>
                            <Typography variant="body2" fontWeight={500}>
                                {realized}
                            </Typography>
                        </Grid>
                    </Grid>
                </Box>
            </Box>
        </Paper>
    );
};
